import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { Progress } from '../ui/progress';

export const PACK_STEPS = [
  { kind: 'devis-ess', label: 'Devis ESS', seconds: 8 },
  { kind: 'dossier-maitre', label: 'Dossier Maître — CP/CG', seconds: 20 },
  { kind: 'pack', label: 'Pack complet (un seul PDF)', seconds: 3 },
];

// Prépare chaque document côté serveur (mis en cache) et suit l'avancement.
export const usePackPreparation = (api, sessionId, onDone) => {
  const [etat, setEtat] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearInterval(timer.current), []);
  const set = (kind, v) => setEtat((e) => ({ ...e, [kind]: { ...e?.[kind], ...v } }));

  const prepare = async () => {
    const debut = Date.now();
    setEtat(Object.fromEntries(PACK_STEPS.map((s) => [s.kind, { pct: 0, status: 'en_cours' }])));
    clearInterval(timer.current);
    timer.current = setInterval(() => setEtat((e) => e && Object.fromEntries(Object.entries(e).map(([k, v]) => {
      const step = PACK_STEPS.find((s) => s.kind === k);
      const t = (Date.now() - debut) / 1000;
      return [k, v.status === 'en_cours' ? { ...v, pct: Math.min(95, Math.round(100 * (1 - Math.exp(-t / step.seconds)))) } : v];
    }))), 300);
    const one = (kind) => api.prepare(sessionId, kind)
      .then(() => set(kind, { pct: 100, status: 'pret' }))
      .catch(() => set(kind, { status: 'erreur' }));
    await Promise.all([one('devis-ess'), one('dossier-maitre')]);
    await one('pack');
    clearInterval(timer.current);
    if (onDone) onDone().catch(() => {});
  };
  return { etat, prepare };
};

const Icon = ({ status }) => {
  if (status === 'pret') return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
  if (status === 'erreur') return <AlertCircle className="h-4 w-4 text-rose-600" />;
  return <Loader2 className="h-4 w-4 animate-spin text-violet-500" />;
};

export const PackProgress = ({ etat }) => {
  if (!etat || Object.values(etat).every((v) => v.status === 'pret')) return null;
  return (
    <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 space-y-2" data-testid="ia-pack-progress">
      {PACK_STEPS.map(({ kind, label }) => {
        const v = etat[kind] || {};
        return (
          <div key={kind} className="space-y-1" data-testid={`ia-pack-progress-${kind}`}>
            <div className="flex items-center justify-between text-xs text-slate-700">
              <span className="flex items-center gap-1.5"><Icon status={v.status} /> {label}</span>
              <span data-testid={`ia-pack-progress-pct-${kind}`}>{v.status === 'erreur' ? 'Erreur' : `${v.pct || 0} %`}</span>
            </div>
            <Progress value={v.pct || 0} className="h-1.5" />
          </div>
        );
      })}
    </div>
  );
};
